import { NodeApi } from '@candulabs/craft';
import React, { useRef } from 'react';

import { CRAFT_ROOT_ID } from 'craft/constants';
import { ValidationError } from 'craft/types';

import { NodeLayer } from './components/NodeLayer';
import { EditorNodeData } from './context/EditorContext';
import { InteractionLayer } from './context/InteractionLayer';

export interface NodeLayerCanvasProps {
  /**
   * fires whenever a drag (provided it is granted to drag) occurs on a node
   * in case many nodes were selected before, AND the drag occurs on one of the selected nodes,
   * this function callbacks all the nodeIds being dragged. Otherwise it callbacks an array of the only nodeId begin dragged
   */
  onDragStart?: (nodeIds: string[]) => void;

  /**
   * fires whenever a drag ends. It does not provide any info on the node being dropped, if a drop was successful,
   * the onMoveNode is used to propagate any action
   */
  onDragEnd?: () => void;

  /**
   * props of the nodes (as a Set) being dragged anywhere in app, that we want to pass here to update the state of this lib.
   * The nodes will be passed into the internal reducer and the NodeLayerCanvas will react the same way as if the same nodes had
   * been dragged directly from within it
   */
  externalDraggedItems?: Set<string>;

  /**
   * props of the nodes (as a Set) being selected anywhere in the app, that we want to pass here to update the state of this lib.
   * The nodes will be passed into the internal reducer and the NodeLayerCanvas will react the same way as if the same nodes had
   * been selected directly from within it
   */
  selectedNodes?: Set<string>;

  /**
   * nodes currently in error state
   */
  errorNodes: ValidationError[];

  /** function returning the NodeApi for a passed nodeId. Needed in order to extract the rules of drag and drop and its relation with other nodes
   * in the editor as a whole. This is pretty much the userEditor().query function
   */
  getNode: (nodeId: string) => NodeApi;

  /**
   * callbacks on whenever one or many nodes get selected inside this lib
   */
  onSelectNode?: (nodeId: string[]) => void;

  /**
   * callbacks at the end of a successful drop event happening within the lib, with the array of nodeIds being dropped, the parentNodeId and
   * the position within the parentNode. Useful to call an action.moveNode from the useEditor hook
   */
  onMoveNode?: (sourceNodeId: string[], parentNodeId: string, index: number) => void;
}

export const NodeLayerCanvas = (props: NodeLayerCanvasProps) => {
  const nodeLayerCanvasRef = useRef<HTMLDivElement>(null);
  return (
    <EditorNodeData getNode={props.getNode} errorNodes={props.errorNodes}>
      <div ref={nodeLayerCanvasRef}>
        <InteractionLayer {...props} nodeLayerCanvasRef={nodeLayerCanvasRef}>
          <NodeLayer nodeId={CRAFT_ROOT_ID} />
        </InteractionLayer>
      </div>
    </EditorNodeData>
  );
};
